import type { CallInfo } from '@compass/client/types/call';
import { type Evaluation, type EvaluationFindingReview } from '@compass/client/types/evaluation';
import type { Scorecard } from '@compass/client/types/scorecard';
import React, { useMemo } from 'react';

import { Badge } from '@/components/ui/badge';
import type { SupportedTimeZone } from '@/constants/date';
import { formatAggregateScore } from '@/pages/qc/utils';
import { useStore } from '@/store';
import { cn } from '@/utils';
import { formatDateTimeCompact, formatDuration } from '@/utils/date';

import {
  computeScorecardSectionResults,
  type FindingEvidenceSnippet,
  formatDurationFromCall,
  formatMetadataValue,
  getCallDateTimeValue,
  getEvaluationDirectionLabel,
  getExtractedDetailFields,
  getFindingEvidenceSnippets,
  getMetadataReasonValue,
  getQcScoreLabel,
  getQcScoreToneClasses,
  getQuestionAnswerLabel,
  getQuestionScoreLabel,
  getReviewerEffectiveFindings,
  getReviewOutcomeMetadataFields,
  getSummaryFieldArrayValue,
  getSummaryFieldValue,
  normalizeQuestionRawScore,
  type SectionQuestionResult,
} from '../qcDetail.utils';

const EMPTY_VALUE = '--';

interface QcEvaluationPdfReportProps {
  agentName?: string | null;
  call?: CallInfo | null;
  className?: string;
  evaluation: Evaluation;
  scorecard?: Scorecard | null;
}

const booleanLabel = (value?: boolean) => {
  if (value === true) return 'True';
  if (value === false) return 'False';
  return EMPTY_VALUE;
};

const getQuestionTitle = (item: SectionQuestionResult) =>
  item.question.title || item.question.label || item.question.metric_key || 'Untitled Question';

const getQuestionSubtitle = (item: SectionQuestionResult) => {
  const title = getQuestionTitle(item);
  if (item.question.title && item.question.label && item.question.label !== title) {
    return item.question.label;
  }
  return item.question.metric_key && item.question.metric_key !== title
    ? item.question.metric_key
    : null;
};

const getReviewAnswerLabel = (
  review: EvaluationFindingReview | undefined,
  scoringType: SectionQuestionResult['question']['scoring_type'],
) => {
  if (review?.raw_score === undefined || review.raw_score === null) {
    return null;
  }

  return getQuestionAnswerLabel({ raw_score: review.raw_score }, scoringType);
};

const getAnswerToneClass = (answer: string | null) => {
  switch (answer) {
    case 'Yes':
      return 'border-positive-border bg-positive-muted text-positive-foreground';
    case 'Partial':
      return 'border-attention-border bg-attention-muted text-attention-foreground';
    case 'No':
      return 'border-critical-border bg-critical-muted text-critical-foreground';
    default:
      return 'border-border bg-muted/50 text-muted-foreground';
  }
};

const renderDate = (value: string | undefined, timeZone: SupportedTimeZone) =>
  value ? formatDateTimeCompact(value, timeZone) : EMPTY_VALUE;

const DetailField: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <div className="min-w-0">
    <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
      {label}
    </div>
    <div className="mt-1 break-words text-[13px] font-medium text-foreground">
      {value || EMPTY_VALUE}
    </div>
  </div>
);

const ReportSection: React.FC<{
  children: React.ReactNode;
  className?: string;
  title: string;
}> = ({ children, className, title }) => (
  <section data-qc-pdf-block className={cn('border-t border-border px-8 py-6', className)}>
    <h2 className="mb-4 text-[13px] font-bold uppercase tracking-wide text-muted-foreground">
      {title}
    </h2>
    {children}
  </section>
);

const FlagPill: React.FC<{ label: string; value?: boolean }> = ({ label, value }) => (
  <div
    className={cn(
      'rounded-md border px-3 py-2',
      value === true
        ? 'border-critical-border bg-critical-muted'
        : value === false
          ? 'border-border bg-muted/50'
          : 'border-border bg-background',
    )}
  >
    <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
      {label}
    </div>
    <div
      className={cn(
        'mt-1 text-sm font-bold',
        value === true
          ? 'text-critical-foreground'
          : value === false
            ? 'text-foreground'
            : 'text-muted-foreground',
      )}
    >
      {booleanLabel(value)}
    </div>
  </div>
);

const EvidenceSnippet: React.FC<{ snippet: FindingEvidenceSnippet }> = ({ snippet }) => (
  <div
    data-qc-pdf-block
    className="break-inside-avoid rounded-md border border-border bg-muted/50 px-3 py-2"
  >
    {snippet.text && (
      <p className="whitespace-pre-wrap text-[12px] leading-relaxed text-foreground">
        {snippet.text}
      </p>
    )}
    {snippet.spans.length > 0 && (
      <div className={cn('flex flex-wrap gap-1.5', snippet.text && 'mt-2')}>
        {snippet.spans.map(span => (
          <span
            key={`${span.start}-${span.end}`}
            className="rounded border border-input bg-background px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground"
          >
            {formatDuration(span.start)}
            {span.end > span.start ? ` - ${formatDuration(span.end)}` : ''}
          </span>
        ))}
      </div>
    )}
    {!snippet.text && snippet.spans.length === 0 && (
      <p className="text-[12px] text-muted-foreground">Evidence span captured.</p>
    )}
  </div>
);

const EvidenceGroup: React.FC<{
  label: string;
  snippets: FindingEvidenceSnippet[];
}> = ({ label, snippets }) => {
  if (snippets.length === 0) {
    return null;
  }

  return (
    <div className="space-y-2">
      <div className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      {snippets.map((snippet, index) => (
        <EvidenceSnippet key={`${label}-${snippet.text}-${index}`} snippet={snippet} />
      ))}
    </div>
  );
};

const QuestionBlock: React.FC<{
  item: SectionQuestionResult;
  questionNumber: number;
  review?: EvaluationFindingReview;
}> = ({ item, questionNumber, review }) => {
  const normalizedRawScore = item.effectiveFinding
    ? normalizeQuestionRawScore(item.effectiveFinding.raw_score, item.question.scoring_type)
    : undefined;
  const aiAnswer = getQuestionAnswerLabel(item.finding, item.question.scoring_type);
  const reviewerAnswer = getReviewAnswerLabel(review, item.question.scoring_type);
  const title = getQuestionTitle(item);
  const subtitle = getQuestionSubtitle(item);
  const aiEvidence = getFindingEvidenceSnippets(item.finding);
  const reviewerEvidence = getFindingEvidenceSnippets(review);
  const hasReviewerReview = !!review;
  const answerLabel = hasReviewerReview ? 'Reviewer' : 'AI';
  const answer = hasReviewerReview ? reviewerAnswer : aiAnswer;
  const evidenceLabel = hasReviewerReview ? 'Reviewer Evidence' : 'AI Evidence';
  const evidence = hasReviewerReview ? reviewerEvidence : aiEvidence;

  return (
    <div
      data-qc-pdf-block
      className="break-inside-avoid border-t border-border py-4 first:border-t-0 first:pt-0"
    >
      <div className="flex items-start gap-3">
        <div
          className={cn(
            'mt-1 h-2.5 w-2.5 shrink-0 rounded-full',
            normalizedRawScore === undefined
              ? 'bg-muted-foreground/30'
              : normalizedRawScore >= 1
                ? 'bg-positive-foreground'
                : normalizedRawScore <= 0
                  ? 'bg-critical-foreground'
                  : 'bg-attention-foreground',
          )}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h4 className="text-[13px] font-bold leading-snug text-foreground">
                {questionNumber}. {title}
              </h4>
              {subtitle && <p className="mt-1 text-[11px] text-muted-foreground">{subtitle}</p>}
            </div>
            <div className="shrink-0 text-right">
              <div
                className={cn('text-[13px] font-bold', getQcScoreToneClasses(normalizedRawScore))}
              >
                {getQuestionScoreLabel(
                  item.effectiveFinding,
                  item.question.scoring_type,
                  item.question.weight ?? 1,
                )}
              </div>
              {item.question.is_critical && (
                <Badge
                  variant="outline"
                  className="mt-1 border-critical-border text-[10px] text-critical-foreground"
                >
                  Critical
                </Badge>
              )}
            </div>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
              {answerLabel}
            </span>
            <span
              className={cn(
                'rounded-md border px-2 py-0.5 text-[11px] font-semibold',
                getAnswerToneClass(answer),
              )}
            >
              {answer || EMPTY_VALUE}
            </span>
          </div>

          <div className="mt-3 grid gap-3">
            {evidence.length === 0 ? (
              <p className="text-[12px] text-muted-foreground">
                No evidence recorded for this finding.
              </p>
            ) : (
              <EvidenceGroup label={evidenceLabel} snippets={evidence} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const chipValues = (values: string[]) =>
  values.length > 0 ? (
    <div className="flex flex-wrap gap-1.5">
      {values.map(value => (
        <span
          key={value}
          className="rounded border border-border bg-muted/50 px-2 py-0.5 text-[11px] font-medium text-muted-foreground"
        >
          {value}
        </span>
      ))}
    </div>
  ) : (
    EMPTY_VALUE
  );

export const QcEvaluationPdfReport = React.forwardRef<HTMLDivElement, QcEvaluationPdfReportProps>(
  ({ agentName, call, className, evaluation, scorecard }, ref) => {
    const { getTimeZone, user } = useStore();
    const timeZone = getTimeZone();
    const scoreLabel = getQcScoreLabel(evaluation.aggregate_score);
    const findingReviews = useMemo(
      () => evaluation.reviews?.findings || {},
      [evaluation.reviews?.findings],
    );
    const reviewerEffectiveFindings = useMemo(
      () => getReviewerEffectiveFindings(evaluation.findings || [], findingReviews),
      [evaluation.findings, findingReviews],
    );
    const sectionResults = useMemo(
      () =>
        computeScorecardSectionResults(
          scorecard?.sections || [],
          evaluation.findings || [],
          evaluation,
          reviewerEffectiveFindings,
        ),
      [evaluation, reviewerEffectiveFindings, scorecard?.sections],
    );
    const extractedFields = getExtractedDetailFields(scorecard, evaluation);
    const reviewOutcomeMetadataFields = getReviewOutcomeMetadataFields(scorecard);
    const callDateTime = getCallDateTimeValue(call);
    const duration = formatDurationFromCall(call);
    const generatedAt = formatDateTimeCompact(new Date(), timeZone);
    const generatedBy = user?.name || user?.email || user?.id || EMPTY_VALUE;

    return (
      <div
        ref={ref}
        className={cn(
          'mx-auto w-[794px] max-w-full bg-background text-foreground shadow-sm',
          className,
        )}
      >
        <header data-qc-pdf-block className="px-8 pb-6 pt-8">
          <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-6">
            <div className="min-w-0">
              <div className="break-words text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
                Call Evaluation
                {evaluation.id ? ` - ${evaluation.id}` : ''}
              </div>
              <h1 className="mt-2 break-words pb-1 text-[18px] font-medium leading-snug tracking-normal text-foreground [overflow-wrap:anywhere]">
                {agentName || 'Evaluation Report'}
              </h1>
            </div>
            <div className="shrink-0 text-right">
              <div
                className={cn(
                  'text-[30px] font-bold leading-none',
                  getQcScoreToneClasses(evaluation.aggregate_score),
                )}
              >
                {formatAggregateScore(evaluation.aggregate_score)}
              </div>
              <div
                className={cn(
                  'mt-2 inline-flex rounded-md px-2.5 py-1 text-[12px] font-bold leading-none',
                  scoreLabel.toneClass,
                )}
              >
                {scoreLabel.content}
              </div>
            </div>
          </div>
        </header>

        <ReportSection title="Evaluation Details">
          <div className="grid grid-cols-4 gap-4">
            <DetailField label="Agent" value={agentName || EMPTY_VALUE} />
            <DetailField
              label="Direction"
              value={getEvaluationDirectionLabel(evaluation.call_direction) || EMPTY_VALUE}
            />
            <DetailField label="Call ID" value={evaluation.call_id || EMPTY_VALUE} />
            <DetailField label="Case ID" value={evaluation.case_record_id || EMPTY_VALUE} />
            <DetailField
              label="Call Date-Time"
              value={callDateTime ? renderDate(callDateTime, timeZone) : EMPTY_VALUE}
            />
            <DetailField label="Duration" value={duration || EMPTY_VALUE} />
            <DetailField
              label="Evaluated At"
              value={renderDate(evaluation.evaluated_at, timeZone)}
            />
            <DetailField label="Created At" value={renderDate(evaluation.created_at, timeZone)} />
          </div>
        </ReportSection>

        <ReportSection title="Review Outcomes">
          <div className="grid grid-cols-2 gap-3">
            <FlagPill label="Escalation Required" value={evaluation.flags?.escalation} />
            <FlagPill label="Compliance Failed" value={evaluation.flags?.compliance_failed} />
          </div>

          {(evaluation.reasons?.escalation || evaluation.reasons?.compliance_failed) && (
            <div className="mt-4 grid gap-3">
              {evaluation.reasons.escalation && (
                <DetailField label="Escalation Reason" value={evaluation.reasons.escalation} />
              )}
              {evaluation.reasons.compliance_failed && (
                <DetailField
                  label="Compliance Failure Reason"
                  value={evaluation.reasons.compliance_failed}
                />
              )}
            </div>
          )}
        </ReportSection>

        {extractedFields.length > 0 && (
          <ReportSection title="Extracted Details">
            <div className="grid gap-4">
              {extractedFields.map(field => (
                <DetailField
                  key={field.key}
                  label={field.label}
                  value={
                    <div className="space-y-2">
                      {chipValues(
                        getSummaryFieldArrayValue(evaluation, [field.key]).map(formatMetadataValue),
                      )}
                      {getMetadataReasonValue(evaluation, field.key) && (
                        <p className="text-[11px] font-normal leading-relaxed text-muted-foreground">
                          {getMetadataReasonValue(evaluation, field.key)}
                        </p>
                      )}
                    </div>
                  }
                />
              ))}
            </div>
          </ReportSection>
        )}

        {reviewOutcomeMetadataFields.length > 0 && (
          <ReportSection title="Additional Review Fields">
            <div className="grid grid-cols-3 gap-4">
              {reviewOutcomeMetadataFields.map(field => (
                <DetailField
                  key={field.key}
                  label={field.label}
                  value={getSummaryFieldValue(evaluation, [field.key]) || EMPTY_VALUE}
                />
              ))}
            </div>
          </ReportSection>
        )}

        <ReportSection title="Scorecard" className="pb-8">
          {sectionResults.length === 0 ? (
            <div className="rounded-md border border-border bg-muted/50 px-4 py-6 text-sm text-muted-foreground">
              No scorecard findings are available for this evaluation yet.
            </div>
          ) : (
            <div className="space-y-5">
              {sectionResults.map((sectionResult, sectionIndex) => (
                <section
                  data-qc-pdf-block
                  key={sectionResult.section.id || sectionIndex}
                  className="break-inside-avoid rounded-md border border-border"
                >
                  <div className="flex items-center justify-between gap-4 border-b border-border bg-muted/50 px-4 py-3">
                    <div>
                      <h3 className="text-sm font-bold text-foreground">
                        {sectionResult.section.title || 'Untitled Section'}
                      </h3>
                      <p className="mt-0.5 text-[11px] text-muted-foreground">
                        {sectionResult.questionCount} questions / {sectionResult.scoredCount} scored
                      </p>
                    </div>
                    <div
                      className={cn(
                        'shrink-0 text-lg font-bold',
                        getQcScoreToneClasses(sectionResult.score),
                      )}
                    >
                      {sectionResult.score === undefined || sectionResult.score === null
                        ? EMPTY_VALUE
                        : formatAggregateScore(sectionResult.score)}
                    </div>
                  </div>

                  <div className="px-4 pt-2">
                    {sectionResult.questions.length === 0 ? (
                      <div className="py-5 text-sm text-muted-foreground">
                        No questions activated for this category.
                      </div>
                    ) : (
                      sectionResult.questions.map((item, itemIndex) => {
                        const metricKey = item.question.metric_key || '';
                        const review = metricKey ? findingReviews[metricKey] : undefined;
                        return (
                          <QuestionBlock
                            key={item.question.id || `${sectionIndex}-${itemIndex}`}
                            item={item}
                            questionNumber={itemIndex + 1}
                            review={review}
                          />
                        );
                      })
                    )}
                  </div>
                </section>
              ))}
            </div>
          )}
        </ReportSection>

        <footer data-qc-pdf-block className="border-t border-border px-8 py-4">
          <div className="grid grid-cols-2 gap-6">
            <div className="min-w-0">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                Generated on
              </div>
              <div className="mt-1 break-words text-[12px] font-medium text-muted-foreground">
                {generatedAt}
              </div>
            </div>
            <div className="min-w-0 justify-self-end text-left">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                Generated by
              </div>
              <div className="mt-1 break-words text-[12px] font-medium text-muted-foreground">
                {generatedBy}
              </div>
            </div>
          </div>
        </footer>
      </div>
    );
  },
);

QcEvaluationPdfReport.displayName = 'QcEvaluationPdfReport';
